import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { adminAPI } from '@/api/client';
import { toast } from 'sonner';

const MessagesManagement = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessages, setSelectedMessages] = useState([]);
  const [filter, setFilter] = useState('all');
  const [batchFilter, setBatchFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [unreadCount, setUnreadCount] = useState(0);
  const [total, setTotal] = useState(0);
  
  // Detail modal
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [audioData, setAudioData] = useState(null);
  const [loadingAudio, setLoadingAudio] = useState(false);
  
  // Delete confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchMessages = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (filter !== 'all') params.status = filter;
      if (batchFilter) params.batch = batchFilter;
      
      const response = await adminAPI.getMessages(params);
      setMessages(response.data.messages || []);
      setTotalPages(response.data.total_pages || 1);
      setUnreadCount(response.data.unread_count || 0);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching messages:', error);
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  }, [page, filter, batchFilter]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  const handleToggleSolved = async (messageId) => {
    try {
      const response = await adminAPI.toggleMessageSolved(messageId);
      if (response.data.success) {
        toast.success(response.data.solved ? 'Marked as solved ✓' : 'Marked as unsolved');
        fetchMessages();
      }
    } catch (error) {
      toast.error('Failed to update message status');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    
    try {
      if (Array.isArray(deleteTarget)) {
        // Bulk delete
        await adminAPI.bulkDeleteMessages(deleteTarget);
        toast.success(`${deleteTarget.length} messages deleted`);
        setSelectedMessages([]);
      } else {
        // Single delete
        await adminAPI.deleteMessage(deleteTarget);
        toast.success('Message deleted');
      }
      setShowDeleteConfirm(false);
      setDeleteTarget(null);
      fetchMessages();
    } catch (error) {
      toast.error('Failed to delete message(s)');
    }
  };

  const handleViewDetail = async (message) => {
    setSelectedMessage(message);
    setShowDetailModal(true);
    setAudioData(null);
    
    // Load audio if exists
    if (message.has_audio) {
      setLoadingAudio(true);
      try {
        const response = await adminAPI.getMessageAudio(message.id);
        setAudioData(response.data);
      } catch (error) {
        console.error('Error loading audio:', error);
      } finally {
        setLoadingAudio(false);
      }
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedMessages(messages.map(m => m.id));
    } else {
      setSelectedMessages([]);
    }
  };

  const handleSelectMessage = (messageId, checked) => {
    if (checked) {
      setSelectedMessages([...selectedMessages, messageId]);
    } else {
      setSelectedMessages(selectedMessages.filter(id => id !== messageId));
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleString('en-BD', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-xl flex items-center gap-2">
              💬 Student Messages
              {unreadCount > 0 && (
                <Badge variant="destructive" className="ml-2">
                  {unreadCount} unread
                </Badge>
              )}
            </CardTitle>
            <div className="text-sm text-gray-500">
              Total: {total} messages
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3 items-center justify-between">
            {/* Filters */}
            <div className="flex gap-3 items-center">
              <Select value={filter} onValueChange={(val) => { setFilter(val); setPage(1); }}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Filter" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Messages</SelectItem>
                  <SelectItem value="unread">Unread</SelectItem>
                  <SelectItem value="solved">Solved</SelectItem>
                </SelectContent>
              </Select>
              
              <Input 
                placeholder="Filter by batch..."
                value={batchFilter}
                onChange={(e) => { setBatchFilter(e.target.value); setPage(1); }}
                className="w-[150px]"
              />
              
              <Button variant="outline" size="sm" onClick={fetchMessages}>
                🔄 Refresh
              </Button>
            </div>
            
            {/* Bulk Actions */}
            {selectedMessages.length > 0 && (
              <div className="flex gap-2 items-center">
                <span className="text-sm text-gray-600">
                  {selectedMessages.length} selected
                </span>
                <Button 
                  variant="destructive" 
                  size="sm"
                  onClick={() => {
                    setDeleteTarget(selectedMessages);
                    setShowDeleteConfirm(true);
                  }}
                >
                  🗑️ Delete Selected
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Messages List */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-gray-500">
              <span className="animate-spin inline-block mr-2">⏳</span>
              Loading messages...
            </div>
          ) : messages.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              📭 No messages found
            </div>
          ) : (
            <div className="divide-y">
              {/* Select All Header */}
              <div className="p-3 bg-gray-50 flex items-center gap-3">
                <Checkbox 
                  checked={selectedMessages.length === messages.length && messages.length > 0}
                  onCheckedChange={handleSelectAll}
                />
                <span className="text-sm text-gray-600 font-medium">Select All</span>
              </div>
              
              {/* Message Items */}
              {messages.map((msg) => (
                <div 
                  key={msg.id} 
                  className={`p-4 hover:bg-gray-50 transition-colors ${!msg.solved ? 'bg-blue-50/30' : ''}`}
                >
                  <div className="flex items-start gap-3">
                    <Checkbox 
                      checked={selectedMessages.includes(msg.id)}
                      onCheckedChange={(checked) => handleSelectMessage(msg.id, checked)}
                    />
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-gray-900">
                          {msg.student_name || 'Unknown'}
                        </span>
                        <Badge variant="outline" className="text-xs">
                          {msg.batch_code || 'N/A'}
                        </Badge>
                        {msg.has_audio && (
                          <Badge className="bg-purple-100 text-purple-700 text-xs">
                            🎤 Voice
                          </Badge>
                        )}
                        {msg.solved && (
                          <Badge className="bg-green-100 text-green-700 text-xs">
                            ✓ Solved
                          </Badge>
                        )}
                      </div>
                      
                      <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                        {msg.message}
                      </p>
                      
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                        <span>📧 {msg.student_email || 'N/A'}</span>
                        <span>📅 {formatDate(msg.sent_at)}</span>
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex gap-2 shrink-0">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleViewDetail(msg)}
                      >
                        👁️ View
                      </Button>
                      <Button 
                        variant={msg.solved ? "outline" : "default"}
                        size="sm"
                        className={msg.solved ? "" : "bg-green-600 hover:bg-green-700"}
                        onClick={() => handleToggleSolved(msg.id)}
                      >
                        {msg.solved ? '↩️ Unsolved' : '✓ Solve'}
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => {
                          setDeleteTarget(msg.id);
                          setShowDeleteConfirm(true);
                        }}
                      >
                        🗑️
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="p-4 border-t flex items-center justify-center gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                disabled={page === 1}
                onClick={() => setPage(p => p - 1)}
              >
                ← Previous
              </Button>
              <span className="text-sm text-gray-600">
                Page {page} of {totalPages}
              </span>
              <Button 
                variant="outline" 
                size="sm" 
                disabled={page === totalPages}
                onClick={() => setPage(p => p + 1)}
              >
                Next →
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              💬 Message Details
              {selectedMessage?.solved && (
                <Badge className="bg-green-100 text-green-700">✓ Solved</Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {selectedMessage && (
            <div className="space-y-4">
              {/* Student Info */}
              <div className="p-4 bg-gray-50 rounded-lg space-y-2">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-gray-500">Name:</span>
                    <span className="ml-2 font-medium">{selectedMessage.student_name}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Batch:</span>
                    <Badge variant="outline" className="ml-2">{selectedMessage.batch_code || 'N/A'}</Badge>
                  </div>
                  <div>
                    <span className="text-gray-500">Email:</span>
                    <span className="ml-2">{selectedMessage.student_email}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Sent:</span>
                    <span className="ml-2">{formatDate(selectedMessage.sent_at)}</span>
                  </div>
                </div>
              </div>
              
              {/* Message Content */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Message:</h4>
                <div className="p-4 bg-white border rounded-lg whitespace-pre-wrap">
                  {selectedMessage.message}
                </div>
              </div>
              
              {/* Voice Message */}
              {selectedMessage.has_audio && (
                <div className="p-4 bg-purple-50 rounded-lg">
                  <h4 className="text-sm font-medium text-purple-700 mb-2 flex items-center gap-2">
                    🎤 Voice Message
                    {selectedMessage.audio_size_kb && (
                      <span className="text-xs text-purple-500">
                        ({selectedMessage.audio_size_kb} KB)
                      </span>
                    )}
                  </h4>
                  {loadingAudio ? (
                    <div className="text-center py-4">
                      <span className="animate-spin">⏳</span> Loading audio...
                    </div>
                  ) : audioData ? (
                    <audio 
                      controls 
                      className="w-full"
                      src={`data:${audioData.content_type};base64,${audioData.audio_data}`}
                    />
                  ) : (
                    <div className="text-center py-4 text-red-500">
                      Failed to load audio
                    </div>
                  )}
                </div>
              )}
              
              {/* Solved Info */}
              {selectedMessage.solved && selectedMessage.solved_at && (
                <div className="text-sm text-green-600 bg-green-50 p-3 rounded-lg">
                  ✓ Solved on {formatDate(selectedMessage.solved_at)}
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowDetailModal(false)}>
              Close
            </Button>
            {selectedMessage && (
              <Button 
                className={selectedMessage.solved ? "" : "bg-green-600 hover:bg-green-700"}
                variant={selectedMessage.solved ? "outline" : "default"}
                onClick={() => {
                  handleToggleSolved(selectedMessage.id);
                  setSelectedMessage({...selectedMessage, solved: !selectedMessage.solved});
                }}
              >
                {selectedMessage.solved ? '↩️ Mark Unsolved' : '✓ Mark as Solved'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="text-red-600">⚠️ Confirm Delete</DialogTitle>
          </DialogHeader>
          <p className="text-gray-600">
            {Array.isArray(deleteTarget) 
              ? `Are you sure you want to delete ${deleteTarget.length} messages? This action cannot be undone.`
              : 'Are you sure you want to delete this message? This action cannot be undone.'}
          </p>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              🗑️ Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MessagesManagement;
